import React from 'react';
import { useTheme } from '../hooks/useTheme';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Divider } from 'primereact/divider';
import { Panel } from 'primereact/panel';
import MediosPagoAccordion from '../components/landing/MediosPagoAccordion';

// Metadata de Next.js removida; usar Helmet si se requiere SEO dinámico

export default function MediosPago() {
    const { theme, toggleTheme } = useTheme();

    const paymentMethods = [
        {
            title: 'Tarjetas de crédito y débito',
            description: 'Visa, Mastercard, American Express',
            icon: 'pi pi-credit-card',
            severity: 'info',
            features: ['Pago inmediato', 'Seguridad SSL', 'Cuotas disponibles'],
            recommended: false
        },
        {
            title: 'Transferencia bancaria',
            description: 'Directa a nuestras cuentas oficiales',
            icon: 'pi pi-building',
            severity: 'success',
            features: ['Validación rápida', 'Sin intermediarios', 'Proceso ágil'],
            recommended: true
        },
        {
            title: 'Pago contra entrega',
            description: 'Disponible en zonas seleccionadas',
            icon: 'pi pi-truck',
            severity: 'warning',
            features: ['Pago al recibir', 'Zonas específicas', 'Sujeto a disponibilidad'],
            recommended: false
        },
        {
            title: 'Plataformas digitales',
            description: 'Yape, Plin, PagoEfectivo',
            icon: 'pi pi-mobile',
            severity: 'help',
            features: ['Pago móvil', 'Rápido y fácil', 'Según canal de ventas'],
            recommended: false
        }
    ];

    const securityFeatures = [
        {
            title: 'Cifrado SSL',
            description: 'Todos los pagos en línea están protegidos con cifrado SSL',
            icon: 'pi pi-shield'
        },
        {
            title: 'Datos seguros',
            description: 'Tu información financiera está completamente protegida',
            icon: 'pi pi-lock'
        },
        {
            title: 'Validación rápida',
            description: 'Procesamiento eficiente de todas las transacciones',
            icon: 'pi pi-check-circle'
        }
    ];

    const cardBrands = [
        { name: 'Visa', icon: 'pi pi-credit-card', color: '#1A1F71' },
        { name: 'Mastercard', icon: 'pi pi-credit-card', color: '#EB001B' },
        { name: 'American Express', icon: 'pi pi-credit-card', color: '#006FCF' }
    ];

    const digitalPlatforms = [
        { name: 'Yape', icon: 'pi pi-mobile', color: '#722F8E' },
        { name: 'Plin', icon: 'pi pi-mobile', color: '#00A651' },
        { name: 'PagoEfectivo', icon: 'pi pi-mobile', color: '#FF6B35' }
    ];

    return (
        <div className={'landing-page theme-' + theme}>
            <Header theme={theme} onToggleTheme={toggleTheme} />
            <div className="surface-ground min-h-screen">
                {/* Hero Section */}
                <div className="surface-section">
                    <div className=" mx-auto px-4">
                        <div className="grid">
                            <div className="col-12">
                                <h1 className="text-4xl lg:text-6xl font-bold text-primary mb-4 line-height-2">
                                    Opciones de Pago Seguras
                                </h1>
                                <p className="text-xl lg:text-2xl text-700 line-height-3 mb-4">
                                    Queremos que compres con tranquilidad y elijas el método de pago que más te convenga. 
                                    Todas nuestras opciones están diseñadas para brindarte seguridad y comodidad.
                                </p>
                                <div className="flex flex-wrap gap-3">
                                    <Badge value="Pagos seguros" severity="success" size='large' />
                                    <Badge value="Múltiples opciones" severity="info" size='large' />   
                                    <Badge value="Cifrado SSL" severity="warning" size='large' />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className=" mx-4">
                {/* Métodos de pago principales */}
                <div className="mb-6">
                    <Card>
                        <div className="p-0 text-center">
                            <h2 className="text-4xl font-bold text-primary mb-4">Métodos de pago disponibles</h2>
                            <div className="grid">
                                {paymentMethods.map((method, index) => (
                                    <div key={index} className="col-12 md:col-6 lg:col-3">
                                        <Card className={`h-full transition-all transition-duration-300 hover:shadow-lg ${
                                            method.recommended ? 'border-2 surface-border' : ''
                                        }`}>
                                            <div className="text-center p-4">
                                                {method.recommended && (
                                                    <Badge value="Recomendado" severity="success" className="mb-3" />
                                                )}
                                                <div className="inline-flex align-items-center justify-content-center w-4rem h-4rem border-circle mb-4">
                                                    <i className={`${method.icon} text-6xl text-primary`}></i>
                                                </div>
                                                <h3 className="text-xl font-semibold text-900 mb-3">{method.title}</h3>
                                                <p className="text-600 line-height-3 mb-4">{method.description}</p>
                                                <div className="mb-4">
                                                    {method.features.map((feature, idx) => (
                                                        <div key={idx} className="flex align-items-center justify-content-center mb-2">
                                                            <i className="pi pi-check text-green-500 mr-2"></i>
                                                            <span className="text-sm text-700">{feature}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                                <Badge value={method.severity === 'success' ? 'Rápido' : method.severity === 'info' ? 'Popular' : method.severity === 'warning' ? 'Limitado' : 'Digital'} severity={method.severity} />
                                            </div>
                                        </Card>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Transferencia bancaria destacada */}
                <div className="mb-6">
                    <Card className="bg-primary border-left-3 border-primary">
                        <div className="p-0">
                            <div className="grid align-items-center">
                                <div className="col-12">
                                    <div className="flex align-items-center mb-3">
                                        <i className="pi pi-star-fill text-primary text-2xl mr-3"></i>
                                        <h2 className="text-3xl font-bold text-900 m-0">Opción recomendada: Transferencia bancaria</h2>
                                    </div>
                                    <p className="text-lg text-700 line-height-3 mb-4">
                                        Esta es la opción recomendada porque la validación de pago es más rápida, 
                                        a diferencia de los pagos con tarjeta que requiere un intermediario que ralentiza 
                                        el proceso de validación de pago y liberación del mismo.
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        <Badge value="Validación rápida" severity="success" />
                                        <Badge value="Sin intermediarios" severity="info" />
                                        <Badge value="Proceso directo" severity="warning" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Tarjetas aceptadas */}
                <div className="mb-4">
                    <Card>
                        <div className="p-0">
                            <h2 className="text-4xl font-bold text-primary mb-4">Tarjetas aceptadas</h2>
                            <div className="grid">
                                <div className="col-12">
                                    <p className="text-lg text-700 mb-4 line-height-3">
                                        Aceptamos las principales tarjetas de crédito y débito con opción de pago en cuotas:
                                    </p>
                                    <div className="grid">
                                        {cardBrands.map((card, index) => (
                                            <div key={index} className="col-12 md:col-4">
                                                <div className="surface-100 border-round p-4 text-center mb-3">
                                                    <i className={`${card.icon} text-6xl mb-3`} style={{ color: card.color }}></i>
                                                    <h4 className="text-lg font-semibold text-900">{card.name}</h4>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-4 p-3 bg-blue-200 border-round border-left-3 border-blue-500">
                                        <p className="text-blue-700 m-0">
                                            <i className="pi pi-info-circle text-blue-500 mr-2"></i>
                                            <strong>Pago en cuotas:</strong> Disponible con tarjetas participantes según términos del banco emisor
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Plataformas digitales */}
                <div className="mb-6">
                    <Card>
                        <div className="p-0 text-center">
                            <h2 className="text-4xl font-bold text-primary mb-2">Plataformas digitales</h2>
                            <div className="grid">
                                <div className="col-12">
                                    <p className="text-lg text-700 mb-4 line-height-3">
                                        Pagos rápidos y seguros desde tu dispositivo móvil:
                                    </p>
                                    <div className="grid">
                                        {digitalPlatforms.map((platform, index) => (
                                            <div key={index} className="col-12 md:col-4">
                                                <div className="surface-100 border-round p-4 text-center mb-3">
                                                    <i className={`${platform.icon} text-6xl mb-3`} style={{ color: platform.color }}></i>
                                                    <h4 className="text-lg font-semibold text-900">{platform.name}</h4>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-4 p-3 bg-orange-200 border-round border-left-3 border-orange-500">
                                        <p className="text-orange-700 m-0">
                                            <i className="pi pi-exclamation-triangle text-orange-500 mr-2"></i>
                                            <strong>Disponibilidad:</strong> Este medio de pago está sujeto al canal de ventas específico
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Seguridad y protección */}
                <div className="mb-6">
                    <Card>
                        <div className="p-0 text-center">
                            <h2 className="text-4xl font-bold text-primary mb-2">Seguridad garantizada</h2>
                            <div className="grid">
                                {securityFeatures.map((feature, index) => (
                                    <div key={index} className="col-12 md:col-4">
                                        <div className="text-center p-4">
                                            <div className="inline-flex align-items-center justify-content-center w-5rem h-5rem bg-green-100 border-circle mb-4">
                                                <i className={`${feature.icon} text-6xl text-green-500`}></i>
                                            </div>
                                            <h3 className="text-xl font-semibold text-900 mb-3">{feature.title}</h3>
                                            <p className="text-600 line-height-3">{feature.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Información adicional */}
                <div className="mb-6">
                    <Card>
                        <div className="p-6">
                            <h2 className="text-4xl font-bold text-900 mb-6">Información adicional</h2>
                            <MediosPagoAccordion>
                                <AccordionTab header="💳 Pago con tarjetas">
                                    <div className="p-4">
                                        <div className="surface-100 border-round p-4">
                                            <h4 className="text-lg font-semibold text-900 mb-3">Características del pago con tarjetas:</h4>
                                            <ul className="list-none p-0 m-0">
                                                <li className="flex align-items-start mb-3">
                                                    <i className="pi pi-check-circle text-green-500 mr-3 mt-1"></i>
                                                    <span className="text-700">Procesamiento seguro con cifrado SSL</span>
                                                </li>
                                                <li className="flex align-items-start mb-3">
                                                    <i className="pi pi-check-circle text-green-500 mr-3 mt-1"></i>
                                                    <span className="text-700">Opción de pago en cuotas según banco emisor</span>
                                                </li>
                                                <li className="flex align-items-start">
                                                    <i className="pi pi-clock text-orange-500 mr-3 mt-1"></i>
                                                    <span className="text-700">Validación puede tomar tiempo adicional por intermediarios</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </AccordionTab>
                                
                                <AccordionTab header="🚚 Pago contra entrega">
                                    <div className="p-0 text-center">
                                        <div className="surface-100 border-round p-4">
                                            <h4 className="text-lg font-semibold text-primary mb-3">Condiciones del pago contra entrega:</h4>
                                            <ul className="list-none p-0 m-0">
                                                <li className="flex align-items-start mb-3">
                                                    <i className="pi pi-map-marker text-blue-500 mr-3 mt-1"></i>
                                                    <span className="text-700">Disponible solo en zonas seleccionadas</span>
                                                </li>
                                                <li className="flex align-items-start mb-3">
                                                    <i className="pi pi-exclamation-triangle text-orange-500 mr-3 mt-1"></i>
                                                    <span className="text-700">Sujeto a la habilitación según canal de ventas</span>
                                                </li>
                                                <li className="flex align-items-start">
                                                    <i className="pi pi-money-bill text-green-500 mr-3 mt-1"></i>
                                                    <span className="text-700">Pago en efectivo al momento de la entrega</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </AccordionTab>
                                
                                <AccordionTab header="🔒 Seguridad de datos">
                                    <div className="p-4">
                                        <div className="surface-100 border-round p-4">
                                            <h4 className="text-lg font-semibold text-900 mb-3">Protección de tu información:</h4>
                                            <ul className="list-none p-0 m-0">
                                                <li className="flex align-items-start mb-3">
                                                    <i className="pi pi-shield text-green-500 mr-3 mt-1"></i>
                                                    <span className="text-700">Cifrado SSL de 256 bits en todas las transacciones</span>
                                                </li>
                                                <li className="flex align-items-start mb-3">
                                                    <i className="pi pi-lock text-blue-500 mr-3 mt-1"></i>
                                                    <span className="text-700">Datos financieros nunca almacenados en nuestros servidores</span>
                                                </li>
                                                <li className="flex align-items-start">
                                                    <i className="pi pi-verified text-purple-500 mr-3 mt-1"></i>
                                                    <span className="text-700">Cumplimiento con estándares internacionales de seguridad</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </AccordionTab>
                            </MediosPagoAccordion>
                        </div>
                    </Card>
                </div>

                {/* Sección de contacto para pagos */}
                <div className="mb-6">
                    <Card>
                        <div className="p-0 text-center">
                            <h2 className="text-4xl font-bold text-900 mb-4">¿Tienes dudas sobre los pagos?</h2>
                            <p className="text-xl text-700 mb-6 line-height-3">
                                Nuestro equipo está disponible para ayudarte con cualquier consulta sobre métodos de pago
                            </p>
                            
                            <div className="grid">
                                <div className="col-12 md:col-4">
                                    <Button 
                                        label="Asesoría de pagos" 
                                        icon="pi pi-headphones" 
                                        className="w-full p-button-outlined p-button-lg mb-3"
                                    />
                                </div>
                                <div className="col-12 md:col-4">
                                    <Button 
                                        label="Verificar pago" 
                                        icon="pi pi-search" 
                                        className="w-full p-button-outlined p-button-lg mb-3"
                                    />
                                </div>
                                <div className="col-12 md:col-4">
                                    <Button 
                                        label="Soporte técnico" 
                                        icon="pi pi-cog" 
                                        className="w-full p-button-lg mb-3"
                                    />
                                </div>
                            </div>
                            
                            <div className="mt-4 p-3 bg-green-200 border-round border-left-3 border-green-500">
                                <p className="text-green-700 m-0">

                                    <i className="pi pi-info-circle text-green-500 mr-2"></i>
                                    <strong>Recuerda:</strong> Para una validación más rápida, recomendamos la transferencia bancaria directa
                                </p>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
            <Footer />
        </div></div>
    );
}